import {Injectable} from '@angular/core';
import {Observable, of} from 'rxjs';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import {environment} from '../../../../environments/environment';
import {SharedService} from '../store/shared-store/shared-storage.service';
import {catchError} from 'rxjs/operators';

export interface FunctionParam {
    name: string;
    description: string;
}

export interface FunctionMetadata {
    id: string;
    name: string;
    description: string;
    active: boolean;
    scriptPath: string;
    mc2AccountId: string;
    params: FunctionParam[] | undefined | null;
}

export interface UpdateFunctionRequest {
    id: string;
    name: string;
    description: string;
    active: boolean;
    scriptPath: string;
    mc2AccountId: string;
}

export interface CreateFunctionRequest {
    name: string;
    description: string;
    active: boolean;
    scriptPath: string;
    mc2AccountId: string;
    params: FunctionParam[] | undefined | null;
}

export interface FunctionExecuteRequest {
    branch: string;
    path: string;
    token: string;
    params: { [index: string]: string };
}

export interface ExecutionResponse {
    result: any;
    tracedLimits: { [index: string]: string };
    error: string;
    info: string;
}

@Injectable({
    providedIn: 'root'
})
export class WasabiFunctionsService {

    private readonly baseUri: string = environment.controlBaseUrl + environment.flowManagerBaseUri;

    constructor(private http: HttpClient, private sharedService: SharedService) {
    }

    public getAllFunctions(): Observable<FunctionMetadata[]> {
        return this.http.get<FunctionMetadata[]>(`${this.createUri()}`).pipe(catchError(err => of([])));
    }

    public deleteFunction(functionId: string): Observable<FunctionMetadata> {
        return this.http.delete<FunctionMetadata>(`${this.createUri()}/${functionId}`);
    }

    public saveFunction(createFunctionRequest: CreateFunctionRequest): Observable<FunctionMetadata> {
        return this.http.post<FunctionMetadata>(`${this.createUri()}`, createFunctionRequest);
    }

    public updateFunction(functionMetadataUpdate: UpdateFunctionRequest): Observable<FunctionMetadata> {
        return this.http.put<FunctionMetadata>(`${this.createUri()}`, functionMetadataUpdate);
    }

    public execute(body: FunctionExecuteRequest): Observable<ExecutionResponse> {
        return this.http.post<ExecutionResponse>(`${this.createUri()}/test`, body)
          .pipe(catchError((error: HttpErrorResponse) => {
              return of(error.error);
          }));
    }

    private createUri(): string {
        const env = this.sharedService.getEnvironment();
        const entityId = this.sharedService.getEntityId();
        const entityType = this.sharedService.getEntityType();
        return `${this.baseUri}/${env}/${entityType}/${entityId}/functions`;
    }
}
